import React from 'react';
import { useLocation } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import Header from './layouts/Header';
import AdminSidebar from './layouts/AdminSidebar';
import { AppRoutes } from './app/router/routes';
import { OrderProvider, OrgaConfigurationProvider } from './app/providers';
import { AuthProvider, useAuth } from './app/providers/AuthContext';

import { OrganizationChangeProvider, useOrganizationChange } from './contexts/OrganizationChangeContext';
import { usePageTracking } from './services/analytics';
import { ThemeProvider } from './shared/contexts/ThemeContext';
import ImpersonationBanner from './components/admin/ImpersonationBanner';

// Wrapper pour extraire l'organizationId de l'URL et le passer au ThemeProvider
function ThemeProviderWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  // Extraire l'organizationId de l'URL
  const organizationId = React.useMemo(() => {
    const orgUrlRegex = /^\/orga\/([^/]+)(\/.*)?$/;
    const orgMatch = orgUrlRegex.exec(location.pathname);
    return orgMatch ? orgMatch[1] : undefined;
  }, [location.pathname]);

  return <ThemeProvider organizationId={organizationId}>{children}</ThemeProvider>;
}

// Wrapper pour charger la configuration d'organisation globalement si présente dans l'URL
function OrgaConfigurationProviderWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const organizationId = React.useMemo(() => {
    const orgUrlRegex = /^\/orga\/([^/]+)(\/.*)?$/;
    const orgMatch = orgUrlRegex.exec(location.pathname);
    return orgMatch ? orgMatch[1] : undefined;
  }, [location.pathname]);

  return (
    <OrgaConfigurationProvider organizationId={organizationId}>
      {children}
    </OrgaConfigurationProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const { isChanging } = useOrganizationChange();
  const { currentUser } = useAuth();

  // Track page views avec Firebase Analytics
  usePageTracking();
  const isLandingPage = location.pathname === '/' || location.pathname === '/auth/login' || location.pathname === '/auth/register';

  // Détecter si on est dans une route avec idorga
  const orgUrlRegex = /^\/orga\/([^/]+)(\/.*)?$/;
  const orgMatch = orgUrlRegex.exec(location.pathname);
  const hasOrganizationInUrl = Boolean(orgMatch);

  // Détecter les routes d'order (sans sidebar)
  const isOrderRoute = location.pathname.includes('/order');
  const isOrderJourney = Boolean(orgMatch && (!orgMatch[2] || orgMatch[2] === '/'));

  // Détecter les routes qui nécessitent la sidebar (tableau de bord et configuration)
  const isAdminRoute = location.pathname.includes('/admin') || location.pathname.includes('/configuration');
  const shouldShowSidebar = hasOrganizationInUrl && !isOrderJourney && !isOrderRoute && isAdminRoute && currentUser;

  return (
    <div
      className={`min-h-screen ${isOrderRoute && !isLandingPage ? '' : isLandingPage ? '' : 'bg-background'}`}
      style={isLandingPage ? { backgroundColor: '#0A0E1A' } : undefined}
    >
      {!isLandingPage && <Header />}
      {!isLandingPage && <ImpersonationBanner />}

      {shouldShowSidebar ? (
        <OrgaConfigurationProvider organizationId={orgMatch ? orgMatch[1] : undefined}>
          <div className="h-screen overflow-hidden">
            <AdminSidebar />
            <main className="fixed top-16 md:left-16 left-0 h-[calc(100vh-64px)] md:w-[calc(100%-64px)] w-full overflow-y-auto z-10 bg-background">
              {isChanging ? (
                <div className="flex flex-col items-center justify-center h-full gap-6">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-20 w-20 border-4 border-primary-20 border-t-primary"></div>
                    <Building2 className="absolute inset-0 m-auto h-8 w-8 text-primary" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Changement d'organisation
                    </h3>
                    <p className="text-sm text-foreground-secondary">
                      Chargement des données de la nouvelle organisation...
                    </p>
                  </div>
                </div>
              ) : (
                <AppRoutes />
              )}
            </main>
          </div>
        </OrgaConfigurationProvider>
      ) : (
        // Layout normal pour les pages sans idorga
        <main className={`${isLandingPage ? 'w-full' : (isOrderRoute ? '' : 'py-8 px-4 container mx-auto max-w-5xl mb-4 bg-background')}`}>
          {isChanging ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6">
              <div className="relative">
                <div className="animate-spin rounded-full h-20 w-20 border-4 border-primary-20 border-t-primary"></div>
                <Building2 className="absolute inset-0 m-auto h-8 w-8 text-primary" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  Changement d'organisation
                </h3>
                <p className="text-sm text-foreground-secondary">
                  Chargement des données de la nouvelle organisation...
                </p>
              </div>
            </div>
          ) : (
            <AppRoutes />
          )}
        </main>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <OrganizationChangeProvider>
        <OrderProvider>
          {/* ThemeProvider racine : garantit que useTheme est toujours disponible (évite l'erreur au chargement/navigation) */}
          <ThemeProvider organizationId={undefined}>
            <ThemeProviderWrapper>
              <OrgaConfigurationProviderWrapper>
                <AppContent />
              </OrgaConfigurationProviderWrapper>
            </ThemeProviderWrapper>
          </ThemeProvider>
        </OrderProvider>
      </OrganizationChangeProvider>
    </AuthProvider>
  );
}

export default App;
